import { useState, useRef, useEffect } from 'react';
import { Paper, Typography, TextField, Box, Grid, Chip, Stack, Button } from '@mui/material';
import { Save } from 'lucide-react';
import Templete from '../../template/Template';
import SimpleBackdrop from '../../components/Loading/SimpleBackdrop';
import { AlertInfo } from '../../components/Alerts/Alert';
import { api } from '../../services/api';
import { useNavigate } from 'react-router-dom';

export default function MessageConfig() {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const [load, setLoad] = useState(false);
    const [id_whatsapp_layout_message_escritorio, setIdLayout] = useState(null);
    const [inputText, setInputText] = useState('');
    const [exempleText, setExempleText] = useState('');
    const [infoLogin] = useState(JSON.parse(sessionStorage.getItem('infoLogin') || ''));
    const [suggestedWords] = useState([
        { value: `{nome_cliente}`, label: 'Nome Cliente', exemple: 'ANTONIO JOSE DA SILVA' },
        { value: `{nome_escritorio}`, label: 'Nome Escritorio', exemple: infoLogin?.nome_escritorio },
        { value: `{cidade_escritorio}`, label: 'Cidade Escritorio', exemple: infoLogin?.cidade_escritorio },
        { value: `{nome_usuario}`, label: 'Advogado', exemple: infoLogin?.nome_usuario },
        { value: `{causa_processual}`, label: 'Causa Processual', exemple: 'Aponsetadoria Idade Rural' },
        { value: `{status_processo}`, label: 'Situação Processo', exemple: 'com Audiência Agendada' },
        { value: `{servico_advogado}`, label: 'Serviços Advocaticios', exemple: 'Previdenciário' },
        { value: `{protocolo_processo}`, label: 'Número do Processo', exemple: '123-456/789' },
    ]);

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputText(event.target.value);
    };

    const handleWordClick = (word: string) => {
        setInputText(prevText => {
            if (prevText.trim() === '') {
                return word;
            }
            return prevText + word;
        });

        // Manter o foco no input após adicionar a palavra
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                // Posicionar o cursor no final do texto
                const input = inputRef.current;
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }
        }, 10);
    };

    function substituirVariaveis() {
        let textoFormatado = inputText;

        // Itera sobre cada objeto no array de dados
        suggestedWords.forEach(word => {
            // Constrói a regex para encontrar o placeholder, escapando caracteres especiais
            // Ex: {nome_cliente}
            const regex = new RegExp(`${word.value}`, 'g');

            // Substitui todas as ocorrências do placeholder pelo label correspondente
            textoFormatado = textoFormatado.replace(regex, word.exemple);
        });

        setExempleText(textoFormatado);
    }

    async function handleSave() {
        setLoad(true);
        try {
            const data = {
                id_whatsapp_layout_message_escritorio,
                message: inputText
            };
            const channel = id_whatsapp_layout_message_escritorio ? `/updatewhatsappmessagelayout` : `/createwhatsappmessagelayout`;
            const response = await api.post(channel, data);
            if(response.data.success) {
                AlertInfo({ icon: 'success', title: 'Dados Gravados' }).then(() => {
                    navigate(`/whatsapp`);
                });
            }else if(response.data.error){
                AlertInfo({ icon: 'error', title: response.data.msg });
            }
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setLoad(false);
    }

    async function loadMessage() {
        setLoad(true);
        try {
            const response = await api.get(`/whatsappmessagelayout`);
            console.log(response);
            if (response.data.message) setInputText(response.data.message);
            if (response.data.id_whatsapp_layout_message_escritorio) setIdLayout(response.data.id_whatsapp_layout_message_escritorio);
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setLoad(false);
    }

    useEffect(() => {
        substituirVariaveis();
    }, [inputText]);

    useEffect(() => {
        loadMessage();
    }, []);

    return (
        <Templete menuActive='whatsapp'>
            <SimpleBackdrop open={load} />
            <Paper
                elevation={3}
                sx={{
                    padding: 4,
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: 2
                }}
            >
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" color="primary" gutterBottom>
                        Adicione a sua mensagem
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            variant="outlined"
                            label="Seu texto aqui"
                            value={inputText}
                            onChange={handleTextChange}
                            placeholder="Comece a digitar..."
                            multiline
                            rows={6}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                        />
                        {exempleText && (
                            <Box
                                sx={{
                                    mt: 3,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Typography variant="h6" color="text.primary" gutterBottom sx={{ mr: 3 }}>
                                    Exemplo de envio:
                                </Typography>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: '#dcf8c6',
                                        borderRadius: '18px 18px 4px 18px',
                                        maxWidth: '50%',
                                        position: 'relative',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        border: 'none'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#303030',
                                            fontSize: '14px',
                                            lineHeight: 1.4,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            textAlign: 'left'
                                        }}
                                    >
                                        {exempleText}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#667781',
                                            fontSize: '11px',
                                            display: 'block',
                                            textAlign: 'right',
                                            mt: 0.5,
                                            lineHeight: 1
                                        }}
                                    >
                                        {new Date().toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                height: '100%',
                                backgroundColor: 'grey.50',
                                borderRadius: 1
                            }}
                        >
                            <Typography variant="h6" color="text.primary" gutterBottom>
                                Variaveis do Processo
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Clique em uma palavra para adicionar o codigo referente ao processo
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {suggestedWords.map((word, index) => (
                                    <Chip
                                        key={index}
                                        label={word.label}
                                        onClick={() => handleWordClick(word.value)}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} container justifyContent="center">
                        <Button
                            startIcon={<Save size={18} />}
                            variant="contained"
                            color="success"
                            onClick={handleSave}
                        >
                            SALVAR MENSAGEM
                        </Button>
                    </Grid>
                </Grid>

            </Paper>
        </Templete>
    );
}